import React, { useImperativeHandle, forwardRef, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Button, Divider, Drawer, Form, Input, Space, Table, Tag, Select } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { getSolutionProducts, CategoryItem, getCategoryThingModel, ThingModelItem } from '@/api';
import { CategoryTemplate } from './types';
import { StandardFuncModal, StandardFuncModalRef } from './StandardFuncModal';
import { TypeSpecMap } from './helper';
import { get } from 'lodash';

const TypeMap: { [key: string]: string } = {
  action: '服务类型',
  event: '事件类型',
  property: '属性类型',
};

export interface CategoryModalRef {
  open: () => void;
}

export interface CategoryModalProps {
  onChange?: (record: CategoryTemplate) => void;
  selectedValue?: string; // 已选择的品类ID，用于回显
}

export const CategoryModal = forwardRef<CategoryModalRef, CategoryModalProps>(({ onChange, selectedValue }, ref) => {
  const standardFuncModalRef = useRef<StandardFuncModalRef>(null);
  const [form] = Form.useForm();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [solutionProducts, setSolutionProducts] = useState<CategoryItem[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [thingModelResponse, setThingModelResponse] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');

  const nameValue = Form.useWatch('name', form);
  const typeValue = Form.useWatch('type', form);

  // 获取解决方案产品列表
  useEffect(() => {
    if (isModalOpen) {
      getSolutionProducts()
        .then((resp: any) => {
          if (resp.success && resp.result) {
            console.log(resp.result);
            setSolutionProducts(resp.result);
            // 如果有已选择的品类，定位到该品类；否则默认选中第一个
            if (resp.result.length > 0) {
              const targetId = selectedValue && resp.result.find((p: CategoryItem) => p.id === selectedValue)
                ? selectedValue
                : resp.result[0].id;
              setActiveTab(targetId);
              loadThingModel(targetId);
            }
          }
        })
        .catch((err: any) => {
          console.error('获取解决方案产品列表失败:', err);
        });
    } else {
      // 关闭弹窗时重置搜索
      setSearchValue('');
    }
  }, [isModalOpen, selectedValue]);

  // 搜索品类
  const handleSearch = useCallback(async (value: string) => {
    setSearchValue(value);
    if (!value.trim()) {
      // 如果搜索值为空，重新加载所有品类
      getSolutionProducts()
        .then((resp: any) => {
          if (resp.success && resp.result) {
            setSolutionProducts(resp.result);
            if (resp.result.length > 0) {
              setActiveTab(resp.result[0].id);
              loadThingModel(resp.result[0].id);
            }
          }
        })
        .catch((err: any) => {
          console.error('获取解决方案产品列表失败:', err);
        });
      return;
    }

    try {
      const resp = await getSolutionProducts({ name: value });
      if (resp.success && resp.result && resp.result.length > 0) {
        setSolutionProducts(resp.result);
        // 定位到第一个搜索结果
        const firstResult = resp.result[0];
        setActiveTab(firstResult.id);
        loadThingModel(firstResult.id);
      } else {
        // 如果没有搜索结果，清空列表
        setSolutionProducts([]);
      }
    } catch (err) {
      console.error('搜索品类失败:', err);
    }
  }, []);

  // 加载物模型数据
  const loadThingModel = (solutionId: string) => {
    setLoading(true);
    getCategoryThingModel(solutionId)
      .then((resp: any) => {
        if (resp.success && resp.result) {
          setThingModelResponse(resp.result);
        }
      })
      .catch((err: any) => {
        console.error('获取物模型失败:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 将接口返回的 properties, events, actions 合并成表格数据源
  const thingModelDataSource = useMemo(() => {
    if (!thingModelResponse) return [];
    const { properties = [], events = [], actions = [] } = thingModelResponse;
    return [
      ...properties.map((item: ThingModelItem) => ({ ...item, type: 'property' as const })),
      ...events.map((item: ThingModelItem) => ({ ...item, type: 'event' as const })),
      ...actions.map((item: ThingModelItem) => ({ ...item, type: 'action' as const })),
    ];
  }, [thingModelResponse]);

  // 根据筛选条件过滤数据
  const filteredDataSource = useMemo(() => {
    if (!thingModelDataSource || !Array.isArray(thingModelDataSource)) return [];
    return thingModelDataSource.filter(({ type, name }) => {
      return type.includes(typeValue || '') && name.includes(nameValue || '');
    });
  }, [thingModelDataSource, nameValue, typeValue]);

  useImperativeHandle(
    ref,
    () => {
      return {
        open() {
          setIsModalOpen(true);
        },
      };
    },
    [],
  );

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <Drawer
      title={'选择品类'}
      placement="right"
      open={isModalOpen}
      width="62%"
      closable={false}
      maskClosable={false}
      destroyOnHidden={false}
      styles={{ 
        header: { height: 51 },
        body: { overflowY: 'auto', overflowX: 'auto' },
      }}
      onClose={handleClose}
      extra={<CloseOutlined onClick={handleClose} />}
      footer={
        <Space>
          <Button onClick={handleClose}>取消</Button>
        </Space>
      }
    >
      <StandardFuncModal ref={standardFuncModalRef} />

      <div style={{ marginBottom: 16, padding: '0 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <Input
          allowClear
          placeholder="请输入名称搜索"
          style={{ width: 200 }}
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <Button
          type="primary"
          style={{ height: 32 }}
          onClick={() => {
            const selectedProduct = solutionProducts.find((product) => product.id === activeTab);
            if (selectedProduct && onChange) {
              // 将 CategoryItem 转换为 CategoryTemplate 格式
              const categoryTemplate: CategoryTemplate = {
                id: selectedProduct.id,
                category_name: selectedProduct.name,
                category_key: selectedProduct.id, // 使用 id 作为 category_key
                scene: '', // scene 暂时为空
              };
              onChange(categoryTemplate);
              handleClose();
            }
          }}
          disabled={!activeTab}
        >
          选择
        </Button>
      </div>

      <div style={{ marginBottom: 20, padding: '0 8px', display: 'flex', flexWrap: 'wrap', width: '900px', minWidth: '900px' }}>
        {solutionProducts.map((product) => {
          // 构建图片URL
          const resolveUrl = (url: string) => {
            if (!url) return '';
            const encodeP = (p: string) => p.split('/').map((seg) => (/[^\x00-\x7F]/.test(seg) ? encodeURIComponent(seg) : seg)).join('/');
            if (/^https?:\/\//i.test(url)) return encodeP(url);
            if (url.startsWith('/')) return encodeP(`${window.location.origin}${url}`);
            if (/^[\w.-]+(?::\d+)?\//.test(url)) return encodeP(`${window.location.protocol}//${url}`);
            return encodeP(`${window.location.origin}/${url}`);
          };
          const imageUrl = product.image_url ? resolveUrl(product.image_url) : null;
          const isSelected = activeTab === product.id;

          return (
            <div
              key={product.id}
              onClick={() => {
                setActiveTab(product.id);
                loadThingModel(product.id);
              }}
              style={{
                margin: '8px 4px',
                borderRadius: 8,
                padding: '8px 16px',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: isSelected ? '2px solid transparent' : '2px solid #e7eaef',
                background: isSelected ? '#fff' : 'transparent',
                backgroundImage: isSelected ? 'linear-gradient(#fff, #fff), linear-gradient(135deg, #1890ff 0%, #722ed1 100%)' : 'none',
                backgroundOrigin: 'border-box',
                backgroundClip: isSelected ? 'padding-box, border-box' : 'padding-box',
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center' }}>
                {imageUrl && (
                  <img
                    src={imageUrl}
                    alt={product.name}
                    style={{
                      width: 20,
                      height: 20,
                      marginRight: 8,
                      objectFit: 'cover',
                    }}
                    onError={(e) => {
                      // 图片加载失败时隐藏图片
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <span
                  style={{
                    color: isSelected ? '#1890ff' : 'inherit',
                    fontWeight: isSelected ? 500 : 'normal',
                  }}
                >
                  {product.name}
                </span>
              </span>
            </div>
          );
        })}
      </div>
      <Divider />
      <Space className="mb15 align-center justify-space-between" wrap style={{ width: '900px', minWidth: '900px', marginBottom: 16 }}>
        <Form layout="inline" form={form} initialValues={{ type: '', name: '' }}>
          <Form.Item key="type" name="type">
            <Select
              placeholder="功能类型 (全部)"
              style={{ minWidth: 180 }}
              options={[
                { label: '功能类型 (全部)', value: '' },
                { label: '属性', value: 'property' },
                { label: '事件', value: 'event' },
                { label: '服务', value: 'action' },
              ]}
            />
          </Form.Item>
          <Form.Item key="name" name="name">
            <Input allowClear placeholder="请输入名称搜索" style={{ width: 200 }} />
          </Form.Item>
        </Form>
      </Space>
      <div style={{ width: '900px', minWidth: '900px' }}>
        <Table
          loading={loading}
          dataSource={filteredDataSource}
          pagination={{
            size: 'small',
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            total: filteredDataSource.length,
          }}
          rowKey="id"
          size="small"
        columns={[
          {
            title: '功能类型',
            key: 'type',
            dataIndex: 'type',
            render: (value: string) => {
              return (
                <>
                  {value === 'property' && <Tag color="green">{TypeMap[value]}</Tag>}
                  {value === 'action' && <Tag color="blue">{TypeMap[value]}</Tag>}
                  {value === 'event' && <Tag color="volcano">{TypeMap[value]}</Tag>}
                </>
              );
            },
          },
          { title: '功能名称', key: 'name', dataIndex: 'name' },
          { title: '标识符', key: 'code', dataIndex: 'code' },
          {
            title: '数据类型',
            key: 'type_spec_type',
            dataIndex: 'type_spec_type',
            render: (value: any, record: ThingModelItem) => {
              const type = get(record, 'type_spec.type', '') as string;
              return TypeSpecMap[type as keyof typeof TypeSpecMap] || '-';
            },
          },
          {
            title: '读写类型',
            key: 'access_mode',
            dataIndex: 'access_mode',
          },
          {
            title: '数据定义',
            key: 'type_spec_specs',
            dataIndex: 'type_spec_specs',
            render: (value: any, record: ThingModelItem) => {
              const type = get(record, 'type_spec.type', '') as string;
              const specs = get(record, 'type_spec.specs', '{}') as string;

              try {
                if (type === 'array') {
                  return '数组个数: ' + JSON.parse(specs)?.size;
                } else if (type === 'text') {
                  return '数据长度: ' + JSON.parse(specs)?.length;
                } else if (type === 'date') {
                  return '整数类型Int64的UTC时间戳 (毫秒)';
                } else if (type === 'bool' || type === 'enum') {
                  return Object.entries(JSON.parse(specs))
                    .map(([k, v]) => `${k}-${v}`)
                    .map((text, index) => (
                      <Tag color="processing" key={index}>
                        {text}
                      </Tag>
                    ));
                } else if (type === 'int' || type === 'float') {
                  const specsObj = JSON.parse(specs);
                  return (
                    <>
                      <div>
                        取值范围: {specsObj?.min}-{specsObj?.max}
                      </div>
                      <div>步长: {specsObj?.step}</div>
                      <div>
                        单位: {specsObj?.unit} ({specsObj?.unitName})
                      </div>
                    </>
                  );
                }
              } catch (e) {
                return '-';
              }
              return '-';
            },
          },
          // {
          //   title: '操作',
          //   key: 'action',
          //   dataIndex: 'action',
          //   fixed: 'right' as const,
          //   render: (value: any, record: ThingModelItem) => {
          //     return (
          //       <Space split={<Text type="secondary">/</Text>}>
          //         <Typography.Link
          //           onClick={() => {
          //             onChange?.(record as any);
          //             handleClose();
          //           }}
          //         >
          //           选择
          //         </Typography.Link>
          //       </Space>
          //     );
          //   },
          // },
        ]}
        />
      </div>
    </Drawer>
  );
});
